/**
 * Maintenance log — records of services the owner has actually completed.
 *
 * Stored in the browser (localStorage) per vehicle, so no account is needed
 * and nothing personal ever leaves the device.
 */
import type { MaintenanceProfile } from "@/lib/maintenance";

export interface LogEntry {
  id: string;
  /** Service name; matches a MaintenanceItem.service when logged from the schedule. */
  service: string;
  /** ISO date (yyyy-mm-dd) the service was performed. */
  date: string;
  /** Odometer reading at the time of service, if known. */
  odometer: number | null;
  /** Mileage milestone this entry satisfies, when logged from the timeline. */
  milestone: number | null;
  notes: string;
}

const PREFIX = "specbase:maintenance-log:";

const storageKey = (vehicleKey: string) => `${PREFIX}${vehicleKey}`;

export function loadLog(vehicleKey: string): LogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(vehicleKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e) => e && typeof e.service === "string" && typeof e.date === "string");
  } catch {
    return [];
  }
}

export function saveLog(vehicleKey: string, entries: LogEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(vehicleKey), JSON.stringify(entries));
  } catch {
    /* storage full or blocked — the log simply won't persist */
  }
}

export function newEntryId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function todayIso() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/** Most recent entry per service name. */
export function lastCompletionByService(entries: LogEntry[]): Map<string, LogEntry> {
  const map = new Map<string, LogEntry>();
  for (const e of entries) {
    const prev = map.get(e.service);
    if (!prev || e.date > prev.date) map.set(e.service, e);
  }
  return map;
}

/** Services logged against a given mileage milestone. */
export function completedAtMilestone(entries: LogEntry[], milestone: number): Set<string> {
  const set = new Set<string>();
  for (const e of entries) if (e.milestone === milestone) set.add(e.service);
  return set;
}

export interface DueService {
  service: string;
  dueAt: number;
  inMiles: number;
  note: string;
  lastDoneAt: number | null;
  lastDoneOn: string | null;
  overdue: boolean;
}

/**
 * Next service due, counting from the last logged completion when there is
 * one, otherwise from the odometer reading.
 */
export function nextDueWithLog(
  profile: MaintenanceProfile,
  entries: LogEntry[],
  odometer: number,
): DueService[] {
  const last = lastCompletionByService(entries);
  return profile.items
    .filter((i) => i.intervalMiles > 0)
    .map((i) => {
      const done = last.get(i.service);
      const base = done?.odometer != null ? done.odometer : null;
      const dueAt =
        base != null
          ? base + i.intervalMiles
          : Math.ceil((odometer + 1) / i.intervalMiles) * i.intervalMiles;
      return {
        service: i.service,
        dueAt,
        inMiles: dueAt - odometer,
        note: i.note,
        lastDoneAt: base,
        lastDoneOn: done?.date ?? null,
        overdue: dueAt <= odometer,
      };
    })
    .sort((a, b) => a.inMiles - b.inMiles);
}
